import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Boxes, Save, BellRing, Send, Wrench, ExternalLink, Truck } from "lucide-react";
import { HistorischeStanden, HistorischeVerkopen, VoorraadGaten } from "@/components/VoorraadHistorie";
import { toast } from "sonner";

export default function InstellingenVoorraad() {
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [horizon, setHorizon] = useState(5);
  const [her, setHer] = useState({ actief: true, ontvangers: "", uur: 8, minuut: 0 });
  const [saving, setSaving] = useState(false);
  const [testBusy, setTestBusy] = useState(false);

  useEffect(() => {
    api.get("/instellingen/voorraad").then((r) => {
      setHorizon(r.data?.bestel_horizon_weken ?? 5);
      if (r.data?.inkoop_herinnering) setHer(r.data.inkoop_herinnering);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/voorraad", {
        bestel_horizon_weken: Number(horizon) || 5,
        inkoop_herinnering: {
          actief: !!her.actief,
          ontvangers: her.ontvangers || "",
          uur: Number(her.uur) || 0,
          minuut: Number(her.minuut) || 0,
        },
      });
      setHorizon(data.bestel_horizon_weken);
      setHer(data.inkoop_herinnering);
      toast.success("Voorraadinstellingen opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Alleen beheerders kunnen dit wijzigen" : "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const testMail = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post("/instellingen/voorraad/herinnering-test");
      toast.success(data.verstuurd
        ? `Herinnering verstuurd naar ${(data.ontvangers || []).join(", ")} (${data.orders} order(s))`
        : "Geen te late inkooporders — er is geen mail verstuurd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Testmail mislukt");
    } finally { setTestBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="instellingen-voorraad">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center">
          <Boxes className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Voorraad</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Besteladvies, inkooporder-herinneringen en voorraadonderhoud op één plek.
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6" data-testid="voorraad-besteladvies">
        <h2 className="font-display text-xl font-medium">Voorraadwaarschuwing</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Een product krijgt de waarschuwing "Bestellen nodig" zodra het bestelmoment binnen dit aantal weken valt
          (bestelmoment = out-of-stock-datum minus de productie-leadtime).
        </p>
        <div className="w-64">
          <Label className="text-xs text-muted-foreground">Waarschuwen vanaf (weken vóór bestelmoment)</Label>
          <Input type="number" min="0" max="52" value={horizon} disabled={!isAdmin}
            onChange={(e) => setHorizon(e.target.value)} data-testid="voorraad-horizon" />
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="voorraad-herinnering">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-medium flex items-center gap-2">
              <BellRing className="h-5 w-5 text-primary" /> Herinnering te late inkooporders
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Dagelijkse e-mail met alle verstuurde of deels ingeboekte inkooporders waarvan de leverdatum
              verstreken is en die nog niet volledig zijn ontvangen.
            </p>
          </div>
          <Switch checked={!!her.actief} disabled={!isAdmin}
            onCheckedChange={(v) => setHer((h) => ({ ...h, actief: v }))}
            data-testid="herinnering-actief" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs text-muted-foreground">E-mailadres(sen) — scheid meerdere met een komma</Label>
            <Input value={her.ontvangers || ""} disabled={!isAdmin}
              placeholder="inkoop@nextproduct.nl, max@nextproduct.nl"
              onChange={(e) => setHer((h) => ({ ...h, ontvangers: e.target.value }))}
              data-testid="herinnering-ontvangers" />
            <p className="text-[11px] text-muted-foreground mt-1">Leeg = alle beheerders van het CRM.</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Tijdstip</Label>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="23" value={her.uur} disabled={!isAdmin}
                onChange={(e) => setHer((h) => ({ ...h, uur: e.target.value }))}
                data-testid="herinnering-uur" />
              <span className="text-muted-foreground">:</span>
              <Input type="number" min="0" max="59" step="5" value={her.minuut} disabled={!isAdmin}
                onChange={(e) => setHer((h) => ({ ...h, minuut: e.target.value }))}
                data-testid="herinnering-minuut" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Nederlandse tijd</p>
          </div>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={testMail} disabled={testBusy}
            data-testid="herinnering-test-btn">
            <Send className="h-4 w-4 mr-2" />{testBusy ? "Versturen…" : "Verstuur nu een test"}
          </Button>
        )}
      </div>

      <HistorischeStanden isAdmin={isAdmin} />
      <DirecteLevering isAdmin={isAdmin} />
      <VoorraadGaten isAdmin={isAdmin} />
      <HistorischeVerkopen isAdmin={isAdmin} />

      <div className="bg-card border border-border rounded-xl p-6" data-testid="voorraad-onderhoud">        <h2 className="font-display text-xl font-medium flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" /> Voorraadonderhoud
        </h2>
        <p className="text-sm text-muted-foreground mt-1 mb-3">
          Herstel dubbele voorraadafboekingen en de backfill van moeder-afboekingen (sticker-model).
        </p>
        <Link to="/instellingen/onderhoud" className="text-sm text-primary hover:underline inline-flex items-center gap-1"
          data-testid="voorraad-onderhoud-link">
          Naar onderhoud <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {isAdmin ? (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="voorraad-instellingen-save">
            <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Alleen beheerders kunnen deze instellingen wijzigen.</p>
      )}
    </div>
  );
}

// Klanten die direct uit de binnenkomende zending worden geleverd. Hun verkopen zijn
// nooit via de eigen voorraad gelopen en worden daarom niet van de voorraadstand
// afgetrokken (wel apart getoond in de mutaties en de historie-grafiek).
function DirecteLevering({ isAdmin }) {
  const [relaties, setRelaties] = useState([]);
  const [horizonNu, setHorizonNu] = useState(5);
  const [q, setQ] = useState("");
  const [treffers, setTreffers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/instellingen/voorraad").then((r) => {
    setRelaties(r.data?.directe_levering_relaties || []);
    setHorizonNu(r.data?.bestel_horizon_weken ?? 5);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length < 2) { setTreffers([]); return; }
      api.get("/organisaties", { params: { q: q.trim() } })
        .then((r) => setTreffers((r.data || []).slice(0, 8))).catch(() => setTreffers([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const bewaar = async (ids) => {
    setBusy(true);
    try {
      const { data } = await api.put("/instellingen/voorraad", {
        bestel_horizon_weken: horizonNu, directe_levering_relaties: ids,
      });
      setRelaties(data?.directe_levering_relaties || []);
      toast.success("Opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Alleen beheerders kunnen dit wijzigen" : "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  const voegToe = (org) => {
    if (relaties.some((r) => r.id === org.id)) { toast.info("Staat er al in"); return; }
    setQ(""); setTreffers([]);
    bewaar([...relaties.map((r) => r.id), org.id]);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="directe-levering">
      <div>
        <h2 className="font-display text-xl font-medium flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" /> Klanten die direct uit de zending geleverd worden
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Deze klanten werden bij binnenkomst van nieuwe producten meteen beleverd — die stuks zijn dus nooit
          in de voorraadstanden verschenen. Hun verkopen worden <strong>niet</strong> van de voorraad afgetrokken
          in de historische lijn en de gaten-berekening, maar wel apart getoond als
          <span className="text-sky-700"> direct geleverd</span> in de mutaties en de grafiek.
        </p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="directe-levering-lijst">
        {relaties.length === 0 && <span className="text-sm text-muted-foreground">Nog geen klanten gekozen.</span>}
        {relaties.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs text-sky-900">
            {r.naam}
            {isAdmin && (
              <button type="button" className="text-sky-700 hover:text-destructive" disabled={busy}
                data-testid={`directe-levering-verwijder-${r.id}`}
                onClick={() => bewaar(relaties.filter((x) => x.id !== r.id).map((x) => x.id))}>×</button>
            )}
          </span>
        ))}
      </div>
      {isAdmin && (
        <div className="relative max-w-md">
          <Label className="text-xs text-muted-foreground">Klant toevoegen</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam…"
            className="h-8" data-testid="directe-levering-zoek" />
          {treffers.length > 0 && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-60 overflow-y-auto py-1">
              {treffers.map((o) => (
                <button key={o.id} type="button" data-testid={`directe-levering-optie-${o.id}`}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
                  onClick={() => voegToe(o)}>
                  {o.naam}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
